import { Button } from '@components/common';
import { Pencil, Trash2 } from 'lucide-react';
import React from 'react';
import { AccountRecord, TaxRateRecord } from '../../../../types/accounting';

interface TaxRatesTableProps {
  taxRates: TaxRateRecord[];
  accounts: AccountRecord[];
  onEdit: (taxRate: TaxRateRecord) => void;
  onDelete: (taxRate: TaxRateRecord) => void;
}

function TaxRateStatusBadge({ status }: { status: TaxRateRecord['status'] }) {
  return (
    <span
      className={`inline-flex rounded-full px-2.5 py-1 text-xs font-semibold ${
        status === 'active'
          ? 'bg-emerald-50 text-emerald-700'
          : 'bg-gray-100 text-gray-700'
      }`}
    >
      {status === 'active' ? 'Active' : 'Inactive'}
    </span>
  );
}

export function TaxRatesTable({
  taxRates,
  accounts,
  onEdit,
  onDelete,
}: TaxRatesTableProps) {
  const accountLookup = new Map(
    accounts.map((account) => [account.id, account]),
  );

  return (
    <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white">
      <div className="max-h-[32rem] overflow-auto">
        <table className="w-full text-sm">
          <thead className="sticky top-0 border-b border-gray-100 bg-gray-50">
            <tr className="text-left">
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Code
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Name
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Rate
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Application
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Status
              </th>
              <th className="px-5 py-3 text-xs font-semibold uppercase tracking-wide text-gray-500">
                Accounts
              </th>
              <th className="px-5 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {taxRates.map((taxRate) => {
              const outputAccount = taxRate.output_account_id
                ? accountLookup.get(taxRate.output_account_id)
                : null;
              const inputAccount = taxRate.input_account_id
                ? accountLookup.get(taxRate.input_account_id)
                : null;

              return (
                <tr key={taxRate.id} className="border-t border-gray-100">
                  <td className="px-5 py-4 font-medium text-gray-900">
                    {taxRate.code}
                  </td>
                  <td className="px-5 py-4 text-gray-700">
                    <div className="font-medium text-gray-900">
                      {taxRate.name}
                    </div>
                    {taxRate.description ? (
                      <div className="text-xs text-gray-500">
                        {taxRate.description}
                      </div>
                    ) : null}
                  </td>
                  <td className="px-5 py-4 text-gray-600">
                    {taxRate.rate.toFixed(4)}%
                  </td>
                  <td className="px-5 py-4 capitalize text-gray-600">
                    {taxRate.application}
                  </td>
                  <td className="px-5 py-4">
                    <TaxRateStatusBadge status={taxRate.status} />
                  </td>
                  <td className="px-5 py-4 text-xs text-gray-600">
                    <div>
                      Output:{' '}
                      {outputAccount
                        ? `${outputAccount.code} - ${outputAccount.name}`
                        : 'None'}
                    </div>
                    <div className="mt-1">
                      Input:{' '}
                      {inputAccount
                        ? `${inputAccount.code} - ${inputAccount.name}`
                        : 'None'}
                    </div>
                  </td>
                  <td className="px-5 py-4">
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="text"
                        size="sm"
                        onClick={() => onEdit(taxRate)}
                      >
                        <Pencil size={14} />
                        Edit
                      </Button>
                      <Button
                        variant="text"
                        size="sm"
                        className="text-red-600 hover:bg-red-50"
                        onClick={() => onDelete(taxRate)}
                      >
                        <Trash2 size={14} />
                        Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
